import { ARMOR_TYPES, type ArmorType, CLASS_ARMOR } from "@/shared/lib/armor";
import {
  evaluateRules,
  type RuleFailure,
  SETUP_RULES,
} from "@/shared/lib/raidRules";
import { RAID_ROLES, type RaidRole } from "@/shared/lib/roles";
import type { WowClass } from "@/shared/lib/wowClass";
import type { BoardCharacter, SplitsResponse } from "./splits.types";

// dnd-kit droppable-id scheme. Splits encode their id; the pool is a fixed sentinel.
const SPLIT_PREFIX = "split:";
export const POOL_DROP_ID = "pool";
export const splitDropId = (splitId: string) => `${SPLIT_PREFIX}${splitId}`;

// Separate id space for the column-reorder draggable/droppable on each split header, so it
// never collides with the character drop targets above.
const SPLIT_REORDER_PREFIX = "split-reorder:";
export const splitReorderId = (splitId: string) =>
  `${SPLIT_REORDER_PREFIX}${splitId}`;

// Reorder an id list by moving one item to another's index.
export function moveInOrder(
  ids: string[],
  fromId: string,
  toId: string,
): string[] {
  const from = ids.indexOf(fromId);
  const to = ids.indexOf(toId);
  if (from === -1 || to === -1 || from === to) return ids;
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export type DropTarget = { kind: "pool" } | { kind: "split"; splitId: string };

export function parseDropTarget(overId: string): DropTarget | null {
  if (overId === POOL_DROP_ID) return { kind: "pool" };
  if (overId.startsWith(SPLIT_PREFIX))
    return { kind: "split", splitId: overId.slice(SPLIT_PREFIX.length) };
  return null;
}

const nameCompare = (a: string, b: string) => a.localeCompare(b, "sv");

// tank → healer → melee → ranged → roleless.
const roleRank = (r: string | null | undefined) => {
  const i = RAID_ROLES.indexOf(r as (typeof RAID_ROLES)[number]);
  return i === -1 ? RAID_ROLES.length : i;
};

// Split columns: by role, then player name.
export function sortByRoleThenName(chars: BoardCharacter[]): BoardCharacter[] {
  return [...chars].sort(
    (a, b) =>
      roleRank(a.raidRole) - roleRank(b.raidRole) ||
      nameCompare(a.playerName, b.playerName),
  );
}

// Unassigned pool: by role, then character name A→Z.
export function sortUnassigned(chars: BoardCharacter[]): BoardCharacter[] {
  return [...chars].sort(
    (a, b) =>
      roleRank(a.raidRole) - roleRank(b.raidRole) ||
      nameCompare(a.name, b.name),
  );
}

export type PoolPlayer = { id: string; name: string };

// Slot layout: roles in board order, roleless last.
const SLOT_ROLES: (RaidRole | null)[] = [...RAID_ROLES, null];
export type RoleSlots = Map<RaidRole | null, number>;

const charRole = (c: BoardCharacter): RaidRole | null =>
  (c.raidRole as RaidRole | null) ?? null;

// Reserved rows per role = the highest count of that role across all splits, so every column
// reserves the same number of tank/healer/melee/ranged slots and the columns line up for
// side-by-side comparison.
export function computeRoleSlots(
  bySplit: Map<string, BoardCharacter[]>,
): RoleSlots {
  const slots: RoleSlots = new Map(SLOT_ROLES.map((r) => [r, 0]));
  for (const chars of bySplit.values()) {
    for (const role of SLOT_ROLES) {
      const count = chars.filter((c) => charRole(c) === role).length;
      if (count > (slots.get(role) ?? 0)) slots.set(role, count);
    }
  }
  return slots;
}

// Lays a split's (role-sorted) characters into the reserved slots, padding each role up to its
// reserved count with nulls (empty placeholder rows).
export function layoutSplitSlots(
  chars: BoardCharacter[],
  slots: RoleSlots,
): (BoardCharacter | null)[] {
  const out: (BoardCharacter | null)[] = [];
  for (const role of SLOT_ROLES) {
    const inRole = chars.filter((c) => charRole(c) === role);
    out.push(...inRole);
    for (let i = inRole.length; i < (slots.get(role) ?? 0); i += 1) {
      out.push(null);
    }
  }
  return out;
}

// Flattens the response into per-split buckets and one sorted unassigned list.
export function buildBoard(data: SplitsResponse): {
  bySplit: Map<string, BoardCharacter[]>;
  unassigned: BoardCharacter[];
  players: PoolPlayer[];
  totalCharacters: number;
} {
  const splitByCharacter = new Map(
    data.assignments.map((a) => [a.characterId, a.splitId]),
  );
  const noteByCharacter = new Map(
    data.notes.map((n) => [n.characterId, n.note]),
  );
  const bySplit = new Map<string, BoardCharacter[]>(
    data.splits.map((s) => [s.id, []]),
  );
  const unassigned: BoardCharacter[] = [];
  let totalCharacters = 0;
  for (const p of data.players) {
    for (const c of p.characters) {
      totalCharacters += 1;
      const bc = {
        ...c,
        playerName: p.name,
        note: noteByCharacter.get(c.id) ?? "",
      };
      const splitId = splitByCharacter.get(c.id);
      const bucket = splitId ? bySplit.get(splitId) : undefined;
      if (bucket) bucket.push(bc);
      else unassigned.push(bc);
    }
  }
  for (const [id, chars] of bySplit) bySplit.set(id, sortByRoleThenName(chars));
  const players = data.players.map((p) => ({ id: p.userId, name: p.name }));
  return {
    bySplit,
    unassigned: sortUnassigned(unassigned),
    players,
    totalCharacters,
  };
}

export function armorCounts(
  chars: BoardCharacter[],
): Record<ArmorType, number> {
  const counts = Object.fromEntries(ARMOR_TYPES.map((a) => [a, 0])) as Record<
    ArmorType,
    number
  >;
  for (const c of chars) counts[CLASS_ARMOR[c.wowClass as WowClass]] += 1;
  return counts;
}

// Full setup rules + same-player backstop.
export function splitRuleFailures(chars: BoardCharacter[]): RuleFailure[] {
  const failures = evaluateRules(
    chars.map((c) => ({
      wowClass: c.wowClass,
      raidRole: c.raidRole ?? null,
      benched: false,
    })),
    SETUP_RULES,
  );
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const c of chars) {
    if (seen.has(c.userId)) dupes.add(c.playerName);
    seen.add(c.userId);
  }
  if (dupes.size > 0) {
    failures.unshift({
      id: "same-player",
      label: `Samma spelare två gånger: ${[...dupes].join(", ")}`,
      severity: "error",
    });
  }
  return failures;
}

// The character (if any) the incoming drag would displace: same player, already in the target split.
export function conflictingCharacter(
  target: BoardCharacter[],
  incoming: BoardCharacter,
): BoardCharacter | undefined {
  return target.find(
    (c) => c.userId === incoming.userId && c.id !== incoming.id,
  );
}
